import { Observable, Subscriber } from "rxjs";
import { Injectable } from "@angular/core";
import { EventSourceMessage, fetchEventSource } from "@microsoft/fetch-event-source";
import Chunk = stages.chatbot.Chunk;
import ErrorMessage = stages.chatbot.ErrorMessage;
import Evidence = stages.chatbot.Evidence;
import Download = stages.chatbot.Download;

export enum MessageType {
	CHUNK = "chunk",
	EVIDENCES = "evidences",
	DOWNLOADS = "downloads",
	RESEND = "resend",
	COMPLETE = "complete",
}

export interface Message {
	type: MessageType;
	chunk?: string;
	evidences?: Evidence[];
	downloads?: Download[];
	summary?: string;
}

@Injectable({ providedIn: "root" })
export class StreamingService {
	abortController?: AbortController;

	stream(
		workspaceId: string,
		processVersion: string,
		chatId: string,
		viewId: string,
		question: string,
	): Observable<Message> {
		const url = `app/chatbot/stream/${chatId}?workspaceId=${workspaceId}&processVersion=${processVersion}&viewId=${viewId}`;
		const ctrl = new AbortController();
		this.abortController = ctrl;
		return new Observable((subscriber) => {
			void fetchEventSource(url, {
				method: "POST",
				headers: {
					"Content-Type": "text/plain",
				},
				body: question,
				signal: this.abortController!.signal,
				onmessage(msg: EventSourceMessage): void {
					StreamingService.processEventMessage(msg, subscriber);
				},
				onclose(): void {
					ctrl.abort();
					subscriber.complete();
				},
				onerror(err: Error): void {
					subscriber.error({
						message: err.message,
						type: "SYSTEM",
					});
					throw err; // abort retry
				},
			});
		});
	}

	abort(): void {
		if (this.abortController !== undefined) {
			this.abortController.abort();
		}
	}

	static processEventMessage(eventMessage: EventSourceMessage, subscriber: Subscriber<Message>): void {
		const data = this.parseMessageData(eventMessage, subscriber.error.bind(subscriber));
		switch (eventMessage.event) {
			case MessageType.CHUNK: {
				subscriber.next({
					type: MessageType.CHUNK,
					chunk: (data as Chunk).m,
				});
				break;
			}
			case MessageType.EVIDENCES: {
				subscriber.next({
					type: MessageType.EVIDENCES,
					evidences: data as Evidence[],
				});
				break;
			}
			case MessageType.DOWNLOADS: {
				subscriber.next({
					type: MessageType.DOWNLOADS,
					downloads: data as Download[],
				});
				break;
			}
			case MessageType.RESEND: {
				subscriber.next({
					type: MessageType.RESEND,
				});
				break;
			}
			case MessageType.COMPLETE: {
				subscriber.next({
					type: MessageType.COMPLETE,
					summary: (data as Chunk).m,
				});
				break;
			}
			case "error": {
				subscriber.error(data as ErrorMessage);
				break;
			}
			default: {
				break; // Ignore unsupported event types
			}
		}
	}

	static parseMessageData(
		eventSourceMessage: EventSourceMessage,
		errorHandler: (err: unknown) => void,
	): Chunk | Download[] | ErrorMessage | Evidence[] | undefined {
		if (eventSourceMessage.event.length === 0 || eventSourceMessage.data.length === 0) {
			return undefined;
		}
		try {
			return JSON.parse(eventSourceMessage.data);
		} catch (error: unknown) {
			errorHandler.call(this, `Malformed JSON ${error}`);
			return undefined;
		}
	}
}
